import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/auth";
import { useQuery } from "@tanstack/react-query";

export type AppRole = 'admin' | 'user' | 'moderator';

export const useUserRoles = () => {
  const { user } = useAuth();

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['user_roles', user?.id],
    queryFn: async () => {
      if (!user) return [] as AppRole[];
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('is_active', true);
      if (error) throw error;
      return (data?.map(r => r.role as AppRole) || []);
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  const isAdmin = useMemo(() => roles.includes('admin'), [roles]);
  const hasRole = (role: AppRole): boolean => roles.includes(role);

  return { roles, isLoading, isAdmin, hasRole };
};